import { BigNumber } from 'bignumber.js'
import { BigNumber as EtherBigNumber } from 'ethers'
import uniq from 'lodash/uniq'
import { getAccount } from 'state/web3/account'
import { getVvsBoostFarmContract } from 'utils/contractHelpers'
import { getVvsMinerMoleAddress } from 'utils/addressHelpers'
import { getLoadedDeserializedFarmFromPid } from 'state/farms/useDeserializedFarms'
import { getFarmVersionFromPid } from 'state/farms/farmsPidToVersion'
import { useUserBoostBalance } from 'state/xVvs/useUserBoostBalance'
import { useBoostableFarmsInfo } from 'state/farmBoost/useBoostableFarmsInfo'
import { useUserBoostedFarmsInfos } from 'state/farmBoost/useUserBoostedFarmsInfos'
import { getLoadedDeserializedFarmUserDataFromPid } from 'state/farms/useDeserializedFarmsUser'
import type { FarmBoostFactorResult } from 'state/farmBoost/useFarmBoostFactor'
import getBoostFactor from 'utils/getBoostFactor'
import { BIG_ZERO } from 'utils/bigNumber'
import { DEFAULT_TOKEN_DECIMAL } from 'config'

export interface PotentialBoostFactorOptions {
  extraBoostBalance?: number
  extraFarmPosition?: number
  nfts?: string[] // tokenIds
  excludeExistingNfts?: boolean
}
export type PotentialFarmBoostFactorResult = Omit<FarmBoostFactorResult, 'loaded'>

export const calcPotentialFarmBoostFactor = async (
  farmId: number,
  options: PotentialBoostFactorOptions = {},
): Promise<PotentialFarmBoostFactorResult> => {
  const farmVersion = getFarmVersionFromPid(farmId)

  const account = getAccount()
  if (account) {
    await getLoadedDeserializedFarmUserDataFromPid(farmId, farmVersion)
  }

  const [farm, boostAllocation, { balance: userExistingBoostBalance }, userExistingNfts] = await Promise.all([
    getLoadedDeserializedFarmFromPid(farmId, farmVersion),
    getBoostAllocation(farmId),
    useUserBoostBalance.getState().getLoadedValue(),
    getExistingStakedNftTokenIds(farmId, options),
  ])

  const extraBoostBalanceBN = options.extraBoostBalance
    ? new BigNumber(options.extraBoostBalance).times(DEFAULT_TOKEN_DECIMAL)
    : BIG_ZERO
  const extraFarmPositionBN = options.extraFarmPosition
    ? new BigNumber(options.extraFarmPosition).times(DEFAULT_TOKEN_DECIMAL)
    : BIG_ZERO
  const nfts = options.nfts || []

  const { allocPoint: baseAllocation, lpTotalStaked } = farm
  const userExistingFarmPosition = account ? farm?.userData?.stakedBalance : BIG_ZERO

  const boostBalanceAmount = extraBoostBalanceBN.plus(userExistingBoostBalance.toString())
  const userFarmPosition = extraFarmPositionBN.plus(userExistingFarmPosition)

  const nftTokenIds = [...nfts, ...userExistingNfts]
  const vvsMinerMoleAddress = getVvsMinerMoleAddress()
  const nftsData = uniq(nftTokenIds).map((tokenId) => ({ tokenContract: vvsMinerMoleAddress, tokenId }))

  const vvsBoostFarmContract = getVvsBoostFarmContract()
  const potentialAdjustedShare = new BigNumber(
    (
      await vvsBoostFarmContract.getPotentialAdjustedShare(
        account ?? '0x0000000000000000000000000000000000000000',
        farmId,
        EtherBigNumber.from(boostBalanceAmount.toString()),
        EtherBigNumber.from(userFarmPosition.toString()),
        nftsData,
      )
    ).toString(),
  )

  return {
    baseAllocation,
    boostAllocation,
    adjustShares: potentialAdjustedShare.div(DEFAULT_TOKEN_DECIMAL),
    boostFactor: getBoostFactor(
      userFarmPosition,
      baseAllocation,
      boostAllocation,
      lpTotalStaked.plus(extraFarmPositionBN),
      potentialAdjustedShare,
    ),
  }
}

const getBoostAllocation = async (farmId: number) => {
  return (await useBoostableFarmsInfo.getState().getLoadedValue()).boostableFarmsFromFarmId[farmId].boostAllocation
}

const getExistingStakedNftTokenIds = async (farmId: number, options: PotentialBoostFactorOptions) => {
  if (options.excludeExistingNfts) return []
  return (await useUserBoostedFarmsInfos.getState().getLoadedValue()).boostedFarmsInfos[farmId]?.boostingNfts ?? []
}
